import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { X, ChevronDown, Phone } from 'lucide-react';

const FAQ_DATA = [
  {
    question: 'How does a crypto-backed loan work?',
    answer: `A crypto-backed loan lets you borrow cash while keeping ownership of your digital assets. Simply use your cryptocurrency (crypto) as collateral and receive funds in U.S. dollars once the loan is approved.

Your crypto collateral stays securely stored with a trusted custodian for the duration of the loan. When your loan is repaid in full, your crypto collateral is released back to you. It's a simple, flexible way to access liquidity without selling your crypto or potentially triggering taxable events.

Please consult a tax advisor to learn of any tax implications regarding this type of transaction.`
  },
  {
    question: 'What are the benefits of using a crypto loan through LendingUSA?',
    answer: (
      <div>
        <p>LendingUSA’s CryptoLending solution is designed for flexibility, speed, and simplicity.</p>
        <ul className="list-disc pl-8 mt-4">
          <li className="mb-2">
            <span className="font-semibold">No minimum credit score</span>
            {' '}
            required
          </li>
          <li className="mb-2">
            <span className="font-semibold">Fast funding,</span>
            {' '}
            typically within 1 to 3 business days
          </li>
          <li className="mb-2">
            <span className="font-semibold">Keep your crypto</span>
            {' '}
            while accessing the cash you need
          </li>
          <li className="mb-2">
            <span className="font-semibold">Transparent terms</span>
            {' '}
            and no hidden fees
          </li>
          <li>
            <span className="font-semibold">Trusted custody</span>
            {' '}
            with a reputable partner
          </li>
        </ul>
      </div>
    )
  },
  {
    question: 'What happens if crypto prices drop or rise?',
    answer: 'If the value of your crypto drops, your Loan-to-Value (LTV) increases. In that case, you may be asked to add more crypto collateral or pay down your debt to prevent liquidation of your crypto collateral. There will always be an attempt to notify you before any such action is required, however this is subject to market price volatility and fluctuations. If your crypto value rises, your LTV automatically decreases, giving you a stronger cushion and greater flexibility.',
  },
  {
    question: 'How do you pay back a crypto loan?',
    answer: (
      <div>
        <p>You can choose between Interest-Only or No Monthly Payment repayment types. They all offer flexibility and transparency. Please note that not all repayment types may be offered at all times.</p>
        <ul className="list-disc pl-8 my-4">
          <li className="mb-2">
            For
            {' '}
            <span className="font-semibold">Interest-Only</span>
            {' '}
            loans, you make smaller monthly payments that approximately cover interest only, with your principal and any remaining interest due at the end of the term.
          </li>
          <li>
            For
            {' '}
            <span className="font-semibold">No Monthly Payment</span>
            {' '}
            loans, you won’t make any monthly payments. The full loan amount, plus interest, is paid at the end of the term.  
          </li>
        </ul>
        You can pay back your loan early at any time, with no penalties or fees. Once your loan is repaid, your crypto collateral is released back to your wallet.  
      </div>
    )
  },
  {
    question: 'How does LendingUSA keep my information secure? How is my crypto kept secure?',
    answer: <>Security is our top priority. LendingUSA protects your personal and financial information with <span className="font-semibold">Cloudflare, Sectigo SSL encryption, </span> and <span className="font-semibold">PCI DSS compliant</span> systems. Your crypto collateral is securely held with a trusted custodian.</>,
  },
];

const FaqOverlay = ({ isOpen, onClose }) => {
  const [expandedIndex, setExpandedIndex] = useState(-1);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleQuestion = (index) => {
    setExpandedIndex(expandedIndex === index ? -1 : index);
  };

  return (
    <div className="fixed inset-0 bg-slate-900 z-[351] overflow-y-auto">
      <div className="min-h-screen px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between items-start mb-8">
            <h1 className="text-xl md:text-3xl font-semibold text-white">
              Frequently <br className="block md:hidden" />Asked Questions
            </h1>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-300 transition-colors p-2 rounded-full border border-white/30 hover:border-white/50"
              aria-label="Close FAQ"
            >
              <div className="block md:hidden">
                <X size={20} />
              </div>
              <div className="hidden md:block">
                <X size={24} />
              </div>
            </button>
          </div>

          <div className="space-y-4 mb-8">
            {FAQ_DATA.map((faq, index) => (
              <div key={index} className="border-b border-gray-700">
                <button
                  onClick={() => toggleQuestion(index)}
                  className="w-full flex items-center justify-between py-4 text-left text-white hover:text-gray-300 transition-colors"
                >
                  <span className="font-medium pr-4">{faq.question}</span>
                  <ChevronDown
                    size={20}
                    className={`flex-shrink-0 transition-transform duration-200 ${
                      expandedIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {expandedIndex === index && (
                  <div className="pb-6 text-gray-300 text-sm whitespace-pre-line">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="space-y-4">
            <a
              className="cursor-pointer w-full py-4 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
              href="tel:+18447216961"
            >
              <Phone size={20} />
              Tap to Call Us Now
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

FaqOverlay.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default FaqOverlay;
